import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PackageCheck, RefreshCw, AlertTriangle } from "lucide-react";

const Adapter = ({ label, value, good }) => (
  <div className="border border-border bg-secondary/30 p-4" data-testid={`adapter-${label.toLowerCase()}`}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
    <div className={`mono mt-2 text-sm ${good ? "text-primary" : "text-muted-foreground"}`}>
      {value || "not detected"}
    </div>
  </div>
);

export const DeliveryPanel = () => {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/delivery");
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const retry = async (id) => {
    try {
      await api.post(`/admin/redemptions/${id}/retry`);
      toast.success("Queued again — next delivery pass will retry it");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!d) return null;
  const a = d.adapters || {};
  const seen = a.seen_at ? new Date(a.seen_at).toLocaleString() : null;

  return (
    <div className="mt-4 border border-border bg-card p-8" data-testid="delivery-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PackageCheck className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">In-game delivery</h3>
          <Badge variant="secondary" className="rounded-none uppercase" data-testid="delivery-pending-badge">
            {fmt(d.pending_total)} pending · {fmt(d.delivered_24h)} delivered 24h
          </Badge>
        </div>
        <Button variant="outline" className="rounded-none" onClick={load} data-testid="refresh-delivery-btn">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
        The resource delivers redeemed rewards automatically every 30s to anyone online and again on
        their next join. Anything it can't hand over stays pending with the exact reason below. In game
        run <span className="mono text-primary">/liquidrp status</span> for adapters or{" "}
        <span className="mono text-primary">/liquidrp test item water 1</span> to test a delivery on
        yourself.
      </p>

      {seen ? (
        <div className="mt-6 grid gap-3 md:grid-cols-4">
          <Adapter label="Framework" value={a.framework} good={!!a.framework && a.framework !== "none"} />
          <Adapter label="Inventory" value={a.inventory} good={!!a.inventory && a.inventory !== "none"} />
          <Adapter label="Database" value={a.mysql} good={a.mysql === "oxmysql"} />
          <Adapter label="Vehicles" value={a.vehicles_table} good={!!a.vehicles_table} />
        </div>
      ) : (
        <div className="mt-6 border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-amber-300" data-testid="no-handshake-warning">
          No handshake yet — start the <span className="mono">liquidrp_loyalty</span> resource on your
          server and it will report which framework, inventory and database it found here.
        </div>
      )}
      {seen && <div className="mt-3 text-xs text-muted-foreground">Reported {seen} · resource v{a.resource_version || "?"}</div>}

      <div className="mt-6 border-t border-border pt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <AlertTriangle className="h-3.5 w-3.5" /> Failed deliveries
        </div>
        {d.failed.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="no-failed-deliveries">
            Nothing has failed to deliver.
          </p>
        ) : (
          <div className="mt-4 divide-y divide-border" data-testid="failed-deliveries-list">
            {d.failed.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm" data-testid={`failed-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">
                    {r.username} · {r.reward_name}
                  </div>
                  <div className="mono mt-1 text-xs text-destructive">{r.last_error}</div>
                  <div className="mono mt-1 text-xs text-muted-foreground">
                    code {r.code || "—"} · class {r.item_class || r.payload || "—"} · {r.attempts} attempt(s)
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => retry(r.id)}
                  data-testid={`retry-delivery-${r.id}`}
                >
                  Retry
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
